/**
 * Erinnerung an Termine dieses Sitzes. Kein Betriebssystem-Hinweis.
 * Die Sitz-Glocke für weitere Sitze bleibt unangetastet.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { portalKalenderSurfaceClass } from './portalAppearance';
import { usePortalAppearance } from './portalAppearanceContext';
import {
  calendarReminderDismissKey,
  calendarReminderInstant,
  calendarReminderIsDismissed,
  calendarReminderMinutes,
  calendarReminderSentence,
  calendarReminderShouldOpen,
  calendarTimeRange,
  dismissCalendarReminder,
  readCalendarReminderOffset,
  type CalendarReminderMinutes,
} from './calendar/portalCalendar';
import { loadCalendarEventsFromPb } from './calendar/portalCalendarPersist';
import { noteSitzGlockeReminder } from './sitzGlockeStore';

type DueReminder = {
  id: string;
  date: string;
  time: string;
  sentence: string;
  missed: boolean;
};

export const PortalKalenderReminder: React.FC<{
  customerId: string;
  kanzleiId?: string;
}> = ({ customerId, kanzleiId = '' }) => {
  const appearance = usePortalAppearance();
  const light = portalKalenderSurfaceClass(appearance);
  const sessionStartedRef = useRef(Date.now());
  const [current, setCurrent] = useState<DueReminder | null>(null);
  const openRef = useRef(false);

  const scan = useCallback(async () => {
    if (!customerId || !kanzleiId) return;
    let events;
    try {
      events = await loadCalendarEventsFromPb({ customerId, kanzleiId });
    } catch {
      return;
    }
    const now = Date.now();
    const due: Array<DueReminder & { fire: number }> = [];
    for (const ev of events) {
      if (!ev.time || !ev.id) continue;
      const mine = ev.customerId === customerId || (ev.attendeeIds || []).includes(customerId);
      if (ev.customerId && !mine) continue;
      const offset = readCalendarReminderOffset(customerId, ev.id);
      if (!calendarReminderMinutes(offset)) continue;
      const key = calendarReminderDismissKey(ev.id, ev.date, ev.time);
      if (
        !calendarReminderShouldOpen({
          offsetMin: offset,
          date: ev.date,
          time: ev.time,
          nowMs: now,
          dismissed: calendarReminderIsDismissed(customerId, key),
        })
      ) {
        continue;
      }
      const fire = calendarReminderInstant(ev.date, ev.time, offset) ?? now;
      const sentence = calendarReminderSentence(ev.title, ev.time, offset as CalendarReminderMinutes);
      due.push({
        id: ev.id,
        date: ev.date,
        time: ev.time,
        sentence,
        missed: fire < sessionStartedRef.current,
        fire,
      });
    }
    due.sort((a, b) => a.fire - b.fire);
    for (const row of due) {
      if (!row.missed) continue;
      noteSitzGlockeReminder({
        seatId: customerId,
        eventId: row.id,
        date: row.date,
        start: calendarTimeRange(row.time).start,
        title: row.sentence,
      });
    }
    if (!openRef.current && due[0]) {
      openRef.current = true;
      setCurrent(due[0]);
    }
  }, [customerId, kanzleiId]);

  useEffect(() => {
    void scan();
    const timer = window.setInterval(() => void scan(), 20000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void scan();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [scan]);

  if (!current) return null;

  const close = () => {
    dismissCalendarReminder(customerId, calendarReminderDismissKey(current.id, current.date, current.time));
    openRef.current = false;
    setCurrent(null);
    void scan();
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center px-4" data-testid="portal-kalender-reminder-layer">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="portal-kalender-reminder-title"
        data-testid="portal-kalender-reminder"
        className={`portal-kalender-reminder w-[min(100vw-2rem,28rem)] rounded-xl border border-violet-400/30 bg-[#07070a] p-4 shadow-2xl ${light}`}
      >
        <h2 id="portal-kalender-reminder-title" className="m-0 text-sm font-semibold text-white">
          Erinnerung
        </h2>
        <p className="m-0 mt-2 text-sm text-slate-100" data-testid="portal-kalender-reminder-sentence">
          {current.sentence}
        </p>
        <button
          type="button"
          className="portal-kalender-reminder-close mt-4 inline-flex rounded-md bg-violet-600 px-3 py-1.5 text-[11px] font-semibold text-white"
          onClick={close}
        >
          Schließen
        </button>
      </div>
    </div>
  );
};
